import { useState } from "react";
import "@/App.css";
import { Toaster } from "sonner";
import { Sparkles, MessagesSquare, Images, LayoutGrid, Rocket, ImagePlus } from "lucide-react";
import StudioPage from "@/pages/StudioPage";
import VisionVaultPage from "@/pages/VisionVaultPage";
import RunwayPage from "@/pages/RunwayPage";
import CollectionPage from "@/pages/CollectionPage";
import LaunchPage from "@/pages/LaunchPage";

const NAV = [
  { id: "studio", label: "Atelier", sub: "Voice & creative dialogue", icon: MessagesSquare },
  { id: "vault", label: "Vision Vault", sub: "Inspiration & analysis", icon: ImagePlus },
  { id: "runway", label: "Runway", sub: "Generate concept looks", icon: Images },
  { id: "collection", label: "Collection", sub: "Line sheet architecture", icon: LayoutGrid },
  { id: "launch", label: "Launch", sub: "Capital & task board", icon: Rocket },
];

function App() {
  const [section, setSection] = useState("studio");
  const [runwaySeed, setRunwaySeed] = useState(null);

  const sendToRunway = (seed) => {
    setRunwaySeed(seed);
    setSection("runway");
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row" data-testid="app-root">
      <Toaster theme="dark" position="top-right" richColors />

      {/* Sidebar */}
      <aside className="lg:w-72 lg:min-h-screen lg:border-r border-b border-white/10 glass lg:sticky lg:top-0 flex-shrink-0 z-40">
        <div className="px-6 py-7 border-b border-white/10">
          <div className="flex items-center gap-2 overline-label">
            <Sparkles size={13} className="text-amber-400" /> AI Haute Atelier
          </div>
          <h1 className="font-serif text-4xl font-light tracking-tight mt-2 leading-none">
            LUTAZZI
          </h1>
          <p className="text-neutral-500 text-xs mt-2 font-mono">Concept House · Est. 2026</p>
        </div>

        <nav className="p-3 lg:py-6 flex lg:flex-col gap-1 overflow-x-auto" data-testid="main-nav">
          {NAV.map((n) => {
            const Icon = n.icon;
            const active = section === n.id;
            return (
              <button
                key={n.id}
                data-testid={`nav-${n.id}`}
                onClick={() => setSection(n.id)}
                className={`group flex items-center gap-3 px-4 py-3 rounded-lg text-left whitespace-nowrap transition-colors duration-200 ${
                  active
                    ? "bg-[#18181C] border border-amber-500/40 text-white"
                    : "border border-transparent text-neutral-400 hover:text-white hover:bg-white/5"
                }`}
              >
                <Icon size={18} className={active ? "text-amber-400" : "text-neutral-500 group-hover:text-amber-400"} />
                <div className="hidden lg:block">
                  <div className="text-sm font-medium">{n.label}</div>
                  <div className="text-[11px] text-neutral-500">{n.sub}</div>
                </div>
                <span className="lg:hidden text-sm font-medium">{n.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="hidden lg:block absolute bottom-0 left-0 right-0 lg:w-72 p-6 border-t border-white/10">
          <p className="text-[11px] text-neutral-600 leading-relaxed">
            Co-created with <span className="text-amber-400/80">ATELIER</span>, your one-and-only
            AI creative director.
          </p>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        {section === "studio" && <StudioPage />}
        {section === "vault" && <VisionVaultPage onUsePalette={sendToRunway} />}
        {section === "runway" && <RunwayPage seed={runwaySeed} onSeedConsumed={() => setRunwaySeed(null)} />}
        {section === "collection" && <CollectionPage />}
        {section === "launch" && <LaunchPage />}
      </main>
    </div>
  );
}

export default App;
